export interface EventItem {
  slug: string;
  kicker: string;
  title: string;
  summary: string;
  date: string;
  readMinutes: number;
  replies: number;
  progress: '已复盘' | '反转中' | '跟进中' | '已澄清';
  tags: string[];
  briefs: string[];
}

export const columns = ['复盘档案', '反转观察', '回应传真', '进展追踪', '吃瓜法务'] as const;

export const progressTone: Record<EventItem['progress'], string> = {
  已复盘: 'bg-primary-500 text-background-50',
  反转中: 'bg-accent-500 text-background-50',
  跟进中: 'bg-secondary-500 text-background-50',
  已澄清: 'bg-foreground-800 text-background-50',
};

export const events: EventItem[] = [
  {
    slug: 'video-72-hours',
    kicker: '复盘档案',
    title: '一条视频如何引爆全网？从发酵到沸腾的七十二小时',
    summary:
      '复盘一次典型事件从首发、转发到热搜的完整链路，拆解传播速度为什么这么快、节点又在哪里开始失控。',
    date: '2026-08-18',
    readMinutes: 9,
    replies: 1284,
    progress: '已复盘',
    tags: ['事件发酵', '传播链', '时间线'],
    briefs: [
      '首发 0-3 小时：原始信息往往来自单一信源，缺乏交叉验证，却在熟人圈率先扩散。',
      '3-12 小时：关键账号转发形成第一波放大，情绪化标题显著提升打开率，也埋下失真伏笔。',
      '12-24 小时：进入热搜与评论区共振阶段，点赞、转发、跟帖相互强化，信息开始滚雪球。',
      '复盘要点：速度并不等于真相，发酵期的每一个转发都在为后续反转积累成本。',
    ],
  },
  {
    slug: 'evidence-collapse',
    kicker: '反转观察',
    title: '反转了！复盘一次“实锤”从诞生到翻车的全过程',
    summary:
      '当截图、录音、聊天记录被奉为“实锤”时，我们距离真相可能更远。本文梳理实锤类信息最常见的三种翻车路径。',
    date: '2026-08-09',
    readMinutes: 8,
    replies: 965,
    progress: '反转中',
    tags: ['实锤', '反转节点', '证据核查'],
    briefs: [
      '路径一：断章取义。一段完整对话被裁剪成只利一方叙事，关键上下文被悄悄丢掉。',
      '路径二：时间错位。旧截图换个时间戳重发，就能制造“刚刚发生”的错觉。',
      '路径三：伪造来源。所谓内部信源、匿名爆料缺乏可核验的出处，只靠情绪驱动。',
      '反转通常发生在第三方实据出现、或当事方以完整文件正面回应之时。',
    ],
  },
  {
    slug: 'crisis-response',
    kicker: '回应传真',
    title: '危机公关是门手艺：一份“多方回应”拆解指南',
    summary:
      '同样是回应，为什么有的灭火、有的引火？用一句话版本、声明版本和行动版本三层结构看懂各方的回应质量。',
    date: '2026-07-30',
    readMinutes: 10,
    replies: 1120,
    progress: '跟进中',
    tags: ['危机公关', '回应话术', '声明拆解'],
    briefs: [
      '第一层：一句话回应。表态越快越简洁越好，含糊其辞往往被解读为默认。',
      '第二层：书面声明。重点看是否给出事实依据、时间节点与可验证的补充信息。',
      '第三层：行动回应。表态之后是否真的整改、致歉、追责，才是判断诚意的标尺。',
      '拆解方法：把回应的“事实陈述”与“情绪表态”分开标记，漏掉事实项就是风险项。',
    ],
  },
  {
    slug: 'rumor-outruns-truth',
    kicker: '进展追踪',
    title: '当谣言跑赢真相：辟谣为什么总是慢半拍？',
    summary:
      '真相还在穿鞋，谣言已经跑遍了全网。从信息发布节奏与审核机制两个角度，看辟谣天然处于劣势的原因。',
    date: '2026-07-21',
    readMinutes: 7,
    replies: 802,
    progress: '已澄清',
    tags: ['谣言', '辟谣', '事实核查'],
    briefs: [
      '谣言抢跑：情绪内容优先触发转发，核实却需要时间，先发优势让谣言占得先机。',
      '审核滞后：正式辟谣往往要等权威信息发布，窗口期正是谣言扩散的高峰。',
      '二次传播：即使辟谣发布，最初的传播路径仍在被旧链接持续触发，需要反复澄清。',
      '给读者的建议：对“惊爆”类内容按下暂停键，主动搜索官方口径再决定是否转发。',
    ],
  },
  {
    slug: 'comment-section-mood',
    kicker: '复盘档案',
    title: '评论区见分晓：情绪如何左右我们对事件的判断',
    summary:
      '一条事件的评论走向往往先于事实定型。本文从群体情绪的角度，分析评论区为什么爱站队、以及如何保持清醒。',
    date: '2026-07-12',
    readMinutes: 6,
    replies: 688,
    progress: '已复盘',
    tags: ['评论区', '群体情绪', '站队'],
    briefs: [
      '情绪共振：与自身经历相近的评论更容易被点赞顶起，形成一边倒的表象。',
      '信息茧房：算法持续推送同类观点，让“大多数人都这么想”成为错觉。',
      '站队的成本：情绪立场先行时，后续即使出现事实反转，改变认知也需要更大力量。',
      '保持清醒的切口：先问“我掌握的是一手还是二手信息”，再决定要不要表态。',
    ],
  },
  {
    slug: 'old-melon-reheat',
    kicker: '反转观察',
    title: '旧账翻新：为什么有些瓜会“回锅”再炒一遍',
    summary:
      '已经收尾的事件为何总能周期性复活？盘点旧闻翻新的典型触发点，以及如何快速判断它是进展还是炒作。',
    date: '2026-07-02',
    readMinutes: 7,
    replies: 733,
    progress: '跟进中',
    tags: ['旧闻翻新', '周期性', '回锅'],
    briefs: [
      '触发点一：当事方更新动态，一句模糊发言就能让旧瓜重新点燃。',
      '触发点二：周年时间点，媒体与博主习惯性做回顾，内容被再次搬运。',
      '触发点三：新证据出现，某条此前被忽略的信息被重新挖出。',
      '判断口诀：先看信息是否“新增”，只有新增事实才算进展，否则只是重复消费。',
    ],
  },
  {
    slug: 'troll-machine',
    kicker: '吃瓜法务',
    title: '谁在带节奏？拆解“水军”与“推手”的运作链路',
    summary:
      '评论区里整齐划一的“巧合”往往并非巧合。本文科普带节奏的常见手法，帮你识别机械刷量与组织化叙事。',
    date: '2026-06-22',
    readMinutes: 8,
    replies: 1540,
    progress: '跟进中',
    tags: ['水军', '推手', '带节奏'],
    briefs: [
      '话术特征：高频重复同一组句式、固定表情与配图，发言时间与频率呈异常规律。',
      '叙事组织：同一套论点在多个账号几乎同步发出，制造“民意共识”的假象。',
      '识别技巧：点进主页看历史内容是否单一、粉丝结构是否异常、评论是否模板化。',
      '法律边界：组织化抹黑与商业诋毁可能触及侵权甚至不正当竞争，涉及取证需谨慎。',
    ],
  },
  {
    slug: 'defamation-line',
    kicker: '吃瓜法务',
    title: '法律是吃瓜的底线：名誉权边界你了解多少',
    summary:
      '围观归围观，有些话一旦说出口就要承担后果。本文梳理在公共事件中评论、转载与曝料需要注意的法律红线。',
    date: '2026-06-10',
    readMinutes: 9,
    replies: 1210,
    progress: '已复盘',
    tags: ['名誉权', '法律边界', '转载'],
    briefs: [
      '事实与观点分开：未经核实的事实性指控风险最高，情绪化观点本身一般不构成侵权。',
      '转载同样担责：转发不实信息并扩大传播，也可能被认定为侵权的参与者。',
      '公开场合仍受保护：即便当事人是公众人物，也并非“随便说”都合法，需要平衡表达自由。',
      '实用建议：对尚未定论的信息使用“据报道、待核实”等限定词，降低误伤可能。',
    ],
  },
  {
    slug: 'follow-up-framework',
    kicker: '进展追踪',
    title: '跟进一件事的正确姿势：怎么追踪才算“跟完”',
    summary:
      '追热点不是看完热搜就结束。本文给出一个可复用的跟进框架：记录、核实、等待、回看，让每次吃瓜都有始有终。',
    date: '2026-05-28',
    readMinutes: 6,
    replies: 542,
    progress: '跟进中',
    tags: ['追踪框架', '方法论', '复盘'],
    briefs: [
      '记录：先建一份事件时间轴，把时间、信源、关键事实按顺序记录下来。',
      '核实：为每一条核心信息标注来源，区分一手信源与二手转述。',
      '等待：在没有新事实前允许结论悬置，不急于给事件盖棺定论。',
      '回看：事件收尾后再通读时间轴，你会清楚看到自己曾被哪一段带偏。',
    ],
  },
];